import type { LocationDatapoint } from "@strtmn/j1939"
import type { TelemetryData } from "@strtmn/sds/types"
import { FaSignal } from "react-icons/fa6"
import { TbActivity, TbEngine } from "react-icons/tb"
import { isProd } from "../../config"
import {
  ClockFettIcon,
  FahrbatterieEvfIcon,
  HydraulicOilTemperatureIcon,
  InverterTemperatureIcon,
  MixerSpeedIcon,
  MotorTemperatureIcon,
  ScrewdriverWrenchSolidIcon,
} from "../icons"
import {
  ConnectivityWidget,
  DashboardColumn,
  DashboardColumns,
  DashboardGroup,
  findTelemetryItem,
  TelemetryNumberWidget,
  type VehicleDashboardProps,
} from "./DashboardUtils"
import * as W from "./Widgets"

function getMotorTempWidget(
  titlePrefix: string,
  telemetry: TelemetryData,
  keyInverter: string,
  keyMotor: string
) {
  const title = `Temperatur ${titlePrefix}motor`
  !isProd && console.debug(`> getMotorTempWidget(${title})`)
  const motorTemp: number | undefined = findTelemetryItem(telemetry, [
    keyMotor,
  ])?.["Value"]
  const inverterTemp: number | undefined = findTelemetryItem(telemetry, [
    keyInverter,
  ])?.["Value"]
  !isProd &&
    console.debug("motorTemp =", motorTemp, " ; inverterTemp =", inverterTemp)
  let hotterComponent: "motor" | "inverter" | undefined
  let keys: string[] = []
  if (motorTemp !== undefined && inverterTemp !== undefined) {
    keys = motorTemp >= inverterTemp ? [keyMotor] : [keyInverter]
    hotterComponent = motorTemp >= inverterTemp ? "motor" : "inverter"
  } else if (motorTemp === undefined) {
    keys = [keyInverter]
    hotterComponent = "inverter"
  } else if (inverterTemp === undefined) {
    keys = [keyMotor]
    hotterComponent = "motor"
  }
  if (motorTemp === undefined && inverterTemp === undefined) {
    keys = []
    hotterComponent = undefined
  }
  !isProd && console.debug("keys =", keys)

  const icon =
    hotterComponent === "inverter" ? (
      <InverterTemperatureIcon />
    ) : (
      <MotorTemperatureIcon />
    )
  return (
    <TelemetryNumberWidget
      title={title}
      IconNode={icon}
      precision={0}
      defaultUnit="°C"
      data={telemetry}
      keys={keys}
    />
  )
}

export function EVertiFeedDashboard({ vehicleStatus }: VehicleDashboardProps) {
  const telemetry = vehicleStatus.Telemetry

  /* BetrStdNextWartung */
  const betrStdNextWartung: number | undefined = findTelemetryItem(telemetry, [
    `${0xd2f3}@${0x00b3}/BetrStdNextWartung`,
  ])?.["Value"]
  const betriebsstunden: number | undefined = findTelemetryItem(telemetry, [
    `${0xd3f3}@${0x00b3}/Betriebsstunden`,
  ])?.["Value"]
  const usageHoursTillNextMaintenance =
    betrStdNextWartung !== undefined && betriebsstunden !== undefined
      ? betrStdNextWartung - betriebsstunden
      : undefined
  /* JahrNextWartung,MonatNextWartung,TagNextWartung */
  const jahrNextWartung = findTelemetryItem(telemetry, [
    `${0xd2f3}@${0x00b3}/JahrNextWartung`,
  ])
  const monatNextWartung = findTelemetryItem(telemetry, [
    `${0xd2f3}@${0x00b3}/MonatNextWartung`,
  ])
  const tagNextWartung = findTelemetryItem(telemetry, [
    `${0xd2f3}@${0x00b3}/TagNextWartung`,
  ])
  const dateOfNextMaintenance =
    jahrNextWartung && monatNextWartung && tagNextWartung
      ? new Date(
          jahrNextWartung["Value"],
          monatNextWartung["Value"] - 1,
          tagNextWartung["Value"]
        )
      : undefined

  const tempHyMotorWidget = getMotorTempWidget(
    "Hydraulik",
    telemetry,
    `${0x01d0}@${0x00ef}/Inv1_IGBTTemp`,
    `${0x01d0}@${0x00ef}/Inv1_MotorTemp`
  )
  const tempMixerMotorWidget = getMotorTempWidget(
    "Mischer",
    telemetry,
    `${0x01d0}@${0x00ff}/Inv1_IGBTTemp`,
    `${0x01d0}@${0x00ff}/Inv1_MotorTemp`
  )

  const location = telemetry["Location"] as unknown as LocationDatapoint
  const hasLocation =
    location &&
    location.Latitude &&
    location.Latitude !== 0 &&
    location.Longitude &&
    location.Longitude !== 0

  // console.debug("telemetry =", telemetry)
  // console.debug(telemetry["Location"])

  return (
    <DashboardColumns>
      <DashboardColumn>
        <DashboardGroup title="Motor & Hydraulik" IconNode={<TbEngine />}>
          {/* Schnecken-Drehzahl */}
          <TelemetryNumberWidget
            IconNode={<MixerSpeedIcon />}
            title="Schnecken-Drehzahl"
            factor={1 / 147}
            precision={0}
            defaultUnit="1/min"
            data={telemetry}
            keys={[`${0x00d0}@${0x00ff}/MCU_MotorRPM`]}
          />
          {/* Hydrauliköltemperatur */}
          <TelemetryNumberWidget
            title="Hydrauliköltemperatur"
            IconNode={<HydraulicOilTemperatureIcon />}
            precision={0}
            defaultUnit="°C"
            data={telemetry}
            keys={[`${0xd2f3}@${0x00b3}/Hydraulikoel_Temperatur`]}
          />
          {/* Temperatur Elektromotoren */}
          {tempHyMotorWidget}
          {tempMixerMotorWidget}
        </DashboardGroup>
      </DashboardColumn>

      <DashboardColumn>
        <DashboardGroup title="Batterie" IconNode={<FahrbatterieEvfIcon />}>
          {/* Batterie Ladezustand */}
          <TelemetryNumberWidget
            title="Batterie Ladezustand"
            IconNode={<FahrbatterieEvfIcon />}
            precision={0}
            defaultUnit="%"
            data={telemetry}
            // Strautmann, fall-back to Othmerding
            keys={[`${0xa103}@${0x00f3}/Pack_SOC`, `${0xc932}@${0x00bb}/SOC`]}
          />
          {/* Batteriespannung */}
          <TelemetryNumberWidget
            title="Batteriespannung"
            IconNode={<FahrbatterieEvfIcon />}
            precision={1}
            defaultUnit="V"
            data={telemetry}
            // Strautmann, fall-back to Othmerding
            keys={[
              `${0xa103}@${0x00f3}/Pack_Inst_Voltage`,
              `${0xca32}@${0x00bb}/TotalVoltage`,
            ]}
          />
        </DashboardGroup>
      </DashboardColumn>

      <DashboardColumn>
        <DashboardGroup title="Betrieb & Wartung" IconNode={<TbActivity />}>
          {/* Betriebsstunden */}
          <TelemetryNumberWidget
            title="Betriebsstunden"
            IconNode={<ClockFettIcon />}
            precision={0}
            defaultUnit="h"
            data={telemetry}
            keys={[`${0xd3f3}@${0x00b3}/Betriebsstunden`]}
          />
          {/* Wartung */}
          {usageHoursTillNextMaintenance !== undefined ? (
            <W.NumberWidget
              title="Betr.Std. bis Wartung"
              IconNode={<ScrewdriverWrenchSolidIcon />}
              precision={0}
              unit="h"
              indicatorTheme={
                usageHoursTillNextMaintenance < 50 ? "error" : undefined
              }
              value={usageHoursTillNextMaintenance}
            />
          ) : (
            <W.TextWidget
              title="Betr.Std. bis Wartung"
              IconNode={<ScrewdriverWrenchSolidIcon />}
              value="Keine Daten"
            />
          )}
          {dateOfNextMaintenance !== undefined ? (
            <W.TextWidget
              title="Wartung am"
              IconNode={<ScrewdriverWrenchSolidIcon />}
              // TODO: set indicatorTheme based on date proximity
              value={dateOfNextMaintenance.toLocaleDateString(
                undefined /* userPrefLang, once it's available */,
                { dateStyle: "medium" }
              )}
            />
          ) : (
            <W.TextWidget
              title="Wartung am"
              IconNode={<ScrewdriverWrenchSolidIcon />}
              value="Keine Daten"
            />
          )}
        </DashboardGroup>
      </DashboardColumn>

      <DashboardColumn>
        <DashboardGroup title="Verbindung" IconNode={<FaSignal />}>
          {/* Datenverbindung */}
          <ConnectivityWidget data={telemetry} />

          {hasLocation ? (
            <W.MapWidget
              title="Standort"
              location={telemetry["Location"] as unknown as LocationDatapoint}
            />
          ) : null}
        </DashboardGroup>
      </DashboardColumn>
    </DashboardColumns>
  )
}
